import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Chip,
  Box,
  Grid,
  TextField,
  MenuItem,
  TablePagination,
  Skeleton,
  IconButton,
  Tooltip,
} from "@material-ui/core";

import { DatePicker, LocalizationProvider } from "@material-ui/lab";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import { es } from "date-fns/locale";

import {
  Visibility as VisibilityIcon,
  Undo as UndoIcon,
} from "@material-ui/icons";

import { useState, useMemo } from "react";
import dayjs from "dayjs";

import { TablaHead } from "src/components/_dashboard/tablas";
import Scrollbar from "src/components/Scrollbar";
import SearchNotFound from "src/components/SearchNotFound";

import { useObtenerConciliacionesQuery } from "src/services/conciliacionApi";

import { formatearMonto, getBancoConfig } from "src/utils/conciliacionHelpers";
import ModalDetalleConciliacion from "./ModalDetalleConciliacion";
import DialogRevertirConciliacion from "./DialogRevertirConciliacion";
import { capitalizarPalabras } from "src/utils/sharedFunctions";
import { formatearFechaDisplay } from "src/utils/formatTime";

// ==================== CONFIGURACIÓN DE COLUMNAS ====================
const TABLE_HEAD = [
  { id: "fecha", label: "Fecha Conciliación", alignRight: false },
  { id: "movimiento", label: "Movimiento", alignRight: false },
  { id: "facturas", label: "Facturas", alignRight: false },
  { id: "tipo", label: "Tipo", alignRight: false },
  { id: "score", label: "Score", alignRight: true },
  { id: "usuario", label: "Usuario", alignRight: false },
  { id: "estado", label: "Estado", alignRight: false },
  { id: "acciones", label: "Acciones", alignRight: true },
];

// ==================== CONFIGURACIONES DE ESTADO ====================
const getTipoConciliacionConfig = (tipo) => {
  const configs = {
    AUTOMATICA: { label: "Automática", color: "#4CAF50", bgcolor: "#E8F5E9" },
    MANUAL: { label: "Manual", color: "#2196F3", bgcolor: "#E3F2FD" },
    PARCIAL: { label: "Parcial", color: "#FF9800", bgcolor: "#FFF3E0" },
  };
  return configs[tipo] || configs.MANUAL;
};

const getEstadoConciliacionConfig = (estado) => {
  const configs = {
    CONCILIADO: { label: "Conciliado", color: "success" },
    REVERTIDO: { label: "Revertido", color: "error" },
  };
  return configs[estado] || configs.CONCILIADO;
};

export default function HistorialTable() {
  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filtros
  const [filtros, setFiltros] = useState({
    estado: "",
    fecha_desde: null,
    fecha_hasta: null,
    banco: "",
    tipo: "",
  });

  const [modalDetalle, setModalDetalle] = useState({
    open: false,
    conciliacion: null,
  });

  const [dialogRevertir, setDialogRevertir] = useState({
    open: false,
    conciliacion: null,
  });

  // ==================== RTK QUERY ====================

  const { data, isLoading, isFetching } = useObtenerConciliacionesQuery({
    estado: filtros.estado || undefined,
    fecha_desde: filtros.fecha_desde
      ? dayjs(filtros.fecha_desde).format("YYYY-MM-DD")
      : undefined,
    fecha_hasta: filtros.fecha_hasta
      ? dayjs(filtros.fecha_hasta).format("YYYY-MM-DD")
      : undefined,
    banco: filtros.banco || undefined,
    tipo: filtros.tipo || undefined,
    page: page + 1,
    limit: rowsPerPage,
  });

  const conciliaciones = useMemo(() => {
    return data?.data || [];
  }, [data]);

  const totalConciliaciones = data?.pagination?.total || 0;

  // ==================== HELPERS ====================

  const contarFiltrosActivos = () => {
    let count = 0;
    if (filtros.estado) count++;
    if (filtros.fecha_desde) count++;
    if (filtros.fecha_hasta) count++;
    if (filtros.banco) count++;
    if (filtros.tipo) count++;
    return count;
  };

  const filtrosActivos = contarFiltrosActivos();

  // Formatear facturas para display
  const formatearFacturas = (detalles) => {
    if (!detalles || detalles.length === 0) return "-";

    const facturas = detalles.map((d) => `${d.tipo_origen} #${d.folio}`);
    return facturas.join(", ");
  };

  // Truncar texto largo
  const truncarTexto = (texto, maxLength = 50) => {
    if (!texto) return "-";
    return texto.length > maxLength
      ? `${texto.substring(0, maxLength)}...`
      : texto;
  };

  // ==================== HANDLERS ====================

  // Paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filtros
  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
    setPage(0); // Reset paginación al cambiar filtros
  };

  const handleLimpiarFiltros = () => {
    setFiltros({
      estado: "",
      fecha_desde: null,
      fecha_hasta: null,
      banco: "",
      tipo: "",
    });
    setPage(0);
  };

  const handleVerDetalle = (conciliacion) => {
    setModalDetalle({
      open: true,
      conciliacion,
    });
  };

  const handleCerrarDetalle = () => {
    setModalDetalle({
      open: false,
      conciliacion: null,
    });
  };

  // Acciones - Revertir
  const handleRevertir = (conciliacion) => {
    setDialogRevertir({
      open: true,
      conciliacion,
    });
  };

  const handleCerrarDialogRevertir = () => {
    setDialogRevertir({
      open: false,
      conciliacion: null,
    });
  };

  // ==================== RENDER ====================

  return (
    <>
      <Card>
        {/* ==================== SECCIÓN DE FILTROS ==================== */}
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Historial de Conciliaciones
            {filtrosActivos > 0 && (
              <Chip
                label={`${filtrosActivos} filtro${
                  filtrosActivos > 1 ? "s" : ""
                }`}
                size="small"
                color="primary"
                sx={{ ml: 2 }}
              />
            )}
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Filtro: Estado */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Estado"
                value={filtros.estado}
                onChange={(e) => handleFiltroChange("estado", e.target.value)}
                size="small"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="CONCILIADO">Conciliado</MenuItem>
                <MenuItem value="REVERTIDO">Revertido</MenuItem>
              </TextField>
            </Grid>

            {/* Filtro: Tipo Conciliación */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Tipo Conciliación"
                value={filtros.tipo}
                onChange={(e) => handleFiltroChange("tipo", e.target.value)}
                size="small"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="AUTOMATICA">Automática</MenuItem>
                <MenuItem value="MANUAL">Manual</MenuItem>
                <MenuItem value="PARCIAL">Parcial</MenuItem>
              </TextField>
            </Grid>

            {/* Filtro: Banco */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Banco"
                value={filtros.banco}
                onChange={(e) => handleFiltroChange("banco", e.target.value)}
                size="small"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="SANTANDER">Santander</MenuItem>
                <MenuItem value="BCI">BCI</MenuItem>
              </TextField>
            </Grid>

            {/* Filtro: Fecha Desde */}
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                <DatePicker
                  label="Fecha Desde"
                  value={filtros.fecha_desde}
                  onChange={(newValue) =>
                    handleFiltroChange("fecha_desde", newValue)
                  }
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" />
                  )}
                  inputFormat="dd/MM/yyyy"
                />
              </LocalizationProvider>
            </Grid>

            {/* Filtro: Fecha Hasta */}
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                <DatePicker
                  label="Fecha Hasta"
                  value={filtros.fecha_hasta}
                  onChange={(newValue) =>
                    handleFiltroChange("fecha_hasta", newValue)
                  }
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" />
                  )}
                  inputFormat="dd/MM/yyyy"
                />
              </LocalizationProvider>
            </Grid>

            {/* Botón Limpiar Filtros */}
            {filtrosActivos > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" height="100%">
                  <Chip
                    label="Limpiar filtros"
                    onClick={handleLimpiarFiltros}
                    onDelete={handleLimpiarFiltros}
                    color="secondary"
                    variant="outlined"
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* ==================== TABLA ==================== */}
        <Scrollbar>
          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <TablaHead headLabel={TABLE_HEAD} />

              <TableBody>
                {/* ==================== LOADING STATE ==================== */}
                {(isLoading || isFetching) && (
                  <>
                    {[...Array(rowsPerPage)].map((_, index) => (
                      <TableRow key={`skeleton-${index}`}>
                        {TABLE_HEAD.map((head) => (
                          <TableCell
                            key={head.id}
                            align={head.alignRight ? "right" : "left"}
                          >
                            <Skeleton variant="text" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </>
                )}

                {/* ==================== DATOS REALES ==================== */}
                {!isLoading && !isFetching && conciliaciones.length > 0 && (
                  <>
                    {conciliaciones.map((conciliacion) => {
                      const tipoConfig = getTipoConciliacionConfig(
                        conciliacion.tipo,
                      );
                      const estadoConfig = getEstadoConciliacionConfig(
                        conciliacion.estado,
                      );
                      const bancoConfig = getBancoConfig(
                        conciliacion.movimiento?.banco,
                      );

                      return (
                        <TableRow key={conciliacion.id} hover>
                          {/* FECHA CONCILIACIÓN */}
                          <TableCell>
                            <Typography variant="body2">
                              {formatearFechaDisplay(
                                conciliacion.fecha_concilia,
                                true,
                              )}
                            </Typography>
                          </TableCell>

                          {/* MOVIMIENTO */}
                          <TableCell>
                            <Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                {bancoConfig && (
                                  <Chip
                                    label={bancoConfig.label}
                                    size="small"
                                    style={{
                                      backgroundColor: bancoConfig.color,
                                      color: "#fff",
                                      fontWeight: 600,
                                    }}
                                  />
                                )}
                                <Typography variant="body2" fontWeight={600}>
                                  {formatearMonto(
                                    conciliacion.movimiento?.monto,
                                  )}
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                              >
                                {truncarTexto(
                                  conciliacion.movimiento?.glosa,
                                  40,
                                )}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* FACTURAS */}
                          <TableCell>
                            <Tooltip
                              title={formatearFacturas(conciliacion.detalles)}
                              arrow
                            >
                              <Typography variant="body2">
                                {conciliacion.detalles?.length || 0} factura
                                {conciliacion.detalles?.length !== 1 ? "s" : ""}
                              </Typography>
                            </Tooltip>
                            <Typography variant="caption" color="textSecondary">
                              {truncarTexto(
                                formatearFacturas(conciliacion.detalles),
                                30,
                              )}
                            </Typography>
                          </TableCell>

                          {/* TIPO CONCILIACIÓN */}
                          <TableCell>
                            <Chip
                              label={tipoConfig.label}
                              size="small"
                              style={{
                                backgroundColor: tipoConfig.bgcolor,
                                color: tipoConfig.color,
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>

                          {/* SCORE */}
                          <TableCell align="right">
                            <Chip
                              label={conciliacion.score || "-"}
                              size="small"
                              color={
                                conciliacion.score >= 90
                                  ? "success"
                                  : conciliacion.score >= 70
                                    ? "warning"
                                    : "default"
                              }
                              variant="outlined"
                            />
                          </TableCell>

                          {/* USUARIO */}
                          <TableCell>
                            <Typography variant="body2">
                              {capitalizarPalabras(
                                conciliacion.conciliado_por,
                              ) || "-"}
                            </Typography>
                          </TableCell>

                          {/* ESTADO */}
                          <TableCell>
                            <Chip
                              label={estadoConfig.label}
                              size="small"
                              color={estadoConfig.color}
                            />
                          </TableCell>

                          {/* ACCIONES */}
                          <TableCell align="right">
                            <Tooltip title="Ver detalle" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleVerDetalle(conciliacion)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {conciliacion.estado === "CONCILIADO" && (
                              <Tooltip title="Revertir conciliación" arrow>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleRevertir(conciliacion)}
                                >
                                  <UndoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                )}

                {/* ==================== EMPTY STATE ==================== */}
                {!isLoading && !isFetching && conciliaciones.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <SearchNotFound
                        searchQuery="conciliaciones"
                        message="No se encontraron conciliaciones con los filtros aplicados"
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>

        {/* ==================== PAGINACIÓN ==================== */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalConciliaciones}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </Card>

      {/* ==================== MODAL DE DETALLE ==================== */}
      {modalDetalle.open && (
        <ModalDetalleConciliacion
          open={modalDetalle.open}
          onClose={handleCerrarDetalle}
          conciliacion={modalDetalle.conciliacion}
        />
      )}

      {dialogRevertir.open && (
        <DialogRevertirConciliacion
          open={dialogRevertir.open}
          onClose={handleCerrarDialogRevertir}
          conciliacion={dialogRevertir.conciliacion}
        />
      )}
    </>
  );
}
